/**
 * iter318 wave 15 — VyomOS marketing site shell.
 * Design tokens from Spec §4.
 * Multi-page shell with routes for Home / Pricing / Contact / About / Privacy / Terms.
 * Nav + footer are CMS-driven when Super Admin has published entries, with
 * a static fallback so first-run installs still look complete.
 */
import { useEffect } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useWebsiteContent } from '../../../hooks/useWebsiteContent';
import SectionRenderer from './SectionRenderer';
import { usePageSeo } from './usePageSeo';
import { GlobalThemeStyles } from './GlobalThemeStyles';
import VyomosHome from './VyomosHome';
import VyomosPricing from './VyomosPricing';
import VyomosContact from './VyomosContact';
import VyomosAbout from './VyomosAbout';
import VyomosLegal from './VyomosLegal';
import VyomosDemo from './VyomosDemo';
import VyomosLogin from './VyomosLogin';

export default function VyomosMarketingShell() {
  useEffect(() => {
    const title = 'VyomOS — The operating system for modern academies and facilities';
    const desc = 'VyomOS runs enrollments, bookings, tournaments, billing and communications for sports academies and facilities — one platform, every workflow.';
    document.title = title;
    // iter335c (P5 SEO) — inject Open Graph + Twitter meta so shared links
    // render a proper card. Idempotent: reuse existing tags by property.
    const setMeta = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', val);
    };
    const url = (typeof window !== 'undefined' && window.location) ? window.location.origin : 'https://vyomos.io';
    setMeta('name', 'description', desc);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'VyomOS');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', `${url}/og-image.png`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', desc);
    setMeta('name', 'twitter:image', `${url}/og-image.png`);
  }, []);

  const { content, loading } = useWebsiteContent('home');
  // Hold the page body until the site settings (global CSS) are in, so it never flashes unstyled.
  const ready = !!content || !loading;

  return (
    <div className="min-h-screen bg-[#F8FBFF] text-[#10233F] flex flex-col" data-testid="vyomos-marketing-shell">
      <MarketingNav content={content} />
      <main className="flex-1">
        {!ready ? <div className="min-h-[60vh]" data-testid="vyomos-shell-loading" /> : (
        <Routes>
          <Route path="/" element={<VyomosHome />} />
          <Route path="/login" element={<VyomosLogin />} />
          {Object.entries(OLD_TO_NEW).map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="/privacy" element={<VyomosLegal kind="privacy" />} />
          <Route path="/terms" element={<VyomosLegal kind="terms" />} />
          <Route path="/:slug" element={<DynamicPage />} />
          <Route path="*" element={<VyomosHome />} />
        </Routes>
        )}
      </main>
      <MarketingFooter content={content} />
    </div>
  );
}

/**
 * iter318 wave 16 — Reusable marketing layout wrapper.
 * Renders the same nav + footer as the shell but lets the caller
 * decide what goes in the middle. Used when App.js Router needs to
 * mount a specific marketing page (e.g. /login) directly, sidestepping
 * React Router v6's descendant-Routes matching quirks.
 */
export function VyomosMarketingLayout({ children }) {
  const { content } = useWebsiteContent('home');
  useEffect(() => {
    document.title = 'VyomOS — The operating system for modern academies and facilities';
  }, []);
  return (
    <div className="min-h-screen bg-[#F8FBFF] text-[#10233F] flex flex-col" data-testid="vyomos-marketing-shell">
      <MarketingNav content={content} />
      <main className="flex-1">{children}</main>
      <MarketingFooter content={content} />
    </div>
  );
}

function MarketingNav({ content }) {
  const loc = useLocation();
  const settings = content?.settings || {};
  const logoUrl = settings.logo_url;
  const logoText = settings.logo_text || 'VyomOS';
  const isActive = (p) => (loc.pathname === p ? 'text-[#1677FF]' : 'text-[#566579] hover:text-[#10233F]');
  const cmsTop = content?.navigation?.top || [];
  const items = cmsTop.length > 0
    ? cmsTop
    : [
        { label: 'Product', href: '/product' },
        { label: 'Solutions', href: '/solution' },
        { label: 'AI', href: '/ai' },
        { label: 'Pricing', href: '/pricevyomos' },
        { label: 'Demo', href: '/demovyomos' },
        { label: 'Contact', href: '/contactus' },
      ];
  return (
    <>
      <GlobalThemeStyles css={settings.global_css} />
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-[#DCE6F0]" data-testid="vyomos-nav">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="vyomos-logo">
          {!content ? <span className="block h-8 w-[120px]" /> : logoUrl
            ? <img src={logoUrl} alt={logoText} className="h-8 w-auto max-w-[160px] object-contain" data-testid="vyomos-logo-img" />
            : <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#1677FF] to-[#55AEEF]" />}
          {content && !logoUrl && <span className="text-lg font-semibold tracking-tight">{logoText}</span>}
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm font-medium">
          {items.map((it) => {
            const external = /^https?:/.test(it.href);
            const cls = isActive(it.href);
            return external ? (
              <a key={it.label} href={it.href} className="text-[#566579] hover:text-[#10233F]">{it.label}</a>
            ) : (
              <Link key={it.label} to={it.href} className={cls}>{it.label}</Link>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline text-sm text-[#566579] hover:text-[#10233F] px-3 py-2" data-testid="nav-login">Login</Link>
          <Link to="/demovyomos" className="text-sm bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-medium rounded-full px-4 py-2 transition" data-testid="nav-book-demo">Try Demo</Link>
        </div>
      </div>
    </nav>
    </>
  );
}

function MarketingFooter({ content }) {
  const groups = content?.footer_groups || [];
  const settings = content?.settings || {};
  const logoUrl = settings.logo_url;
  const logoText = settings.logo_text || 'VyomOS';
  const tagline = settings.footer_tagline || 'Software with room to grow.';
  const useCms = groups.length > 0;
  return (
    <footer className="bg-white border-t border-[#DCE6F0] mt-16" data-testid="vyomos-footer">
      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
        <div className="col-span-2">
          <div className="flex items-center gap-2 mb-3">
            {!content ? <span className="block h-7 w-[110px]" /> : logoUrl
              ? <img src={logoUrl} alt={logoText} className="h-7 w-auto max-w-[140px] object-contain" data-testid="vyomos-footer-logo-img" />
              : <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#1677FF] to-[#55AEEF]" />}
            {content && !logoUrl && <span className="font-semibold">{logoText}</span>}
          </div>
          <p className="text-[#566579] mb-4 max-w-xs" data-testid="vyomos-footer-tagline">{tagline}</p>
        </div>
        {useCms ? (
          groups.map((g) => (
            <FooterCol
              key={g.id}
              title={g.title}
              items={(g.links || []).map((l) => [l.label, l.href])}
            />
          ))
        ) : (
          <>
            <FooterCol title="Product" items={[['Features','/features'],['AI','/ai'],['Pricing','/pricevyomos'],['White-Label Portals','/product'],['Demo','/demovyomos'],['Customer Login','/login']]} />
            <FooterCol title="Solutions" items={[['Academies','/solution'],['Facilities','/solution'],['Multi-Location','/solution'],['Custom Workflows','/contactus']]} />
            <FooterCol title="Company" items={[['About','/about'],['Contact','/contactus'],['Privacy','/privacy'],['Terms','/terms']]} />
          </>
        )}
      </div>
      <div className="border-t border-[#DCE6F0] py-5 text-center text-xs text-[#566579]">
        VyomOS — The operating system for modern academies and facilities. © {new Date().getFullYear()}
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <h4 className="font-semibold text-[#10233F] mb-3">{title}</h4>
      <ul className="space-y-2">
        {items.map(([label, href]) => (
          <li key={label}>
            {String(href).startsWith('http') ? (
              <a href={href} className="text-[#566579] hover:text-[#1677FF]">{label}</a>
            ) : (
              <Link to={href} className="text-[#566579] hover:text-[#1677FF]">{label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


/**
 * iter336 — Generic renderer for custom pages created in the CMS
 * Pages panel. Fetches the page's published sections and renders them
 * via SectionRenderer. Shows a friendly placeholder when the page has
 * no content yet (or the slug isn't a published page).
 */
// Pages moved to their canonical addresses (iter404).
const OLD_TO_NEW = { '/pricing': '/pricevyomos', '/demo': '/demovyomos', '/contact': '/contactus', '/solutions': '/solution' };

function DynamicPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { content, loading } = useWebsiteContent(slug);
  const sections = content?.sections || [];
  usePageSeo(slug, { empty: !loading && sections.length === 0 });
  if (loading && !content) {
    return <div className="min-h-[60vh]" data-testid="vyomos-dynamic-loading" />;
  }
  if (sections.length > 0) {
    return (
      <div data-testid={`vyomos-page-${slug}`}>
        {sections.map((s) => <SectionRenderer key={s.id} section={s} />)}
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="vyomos-page-empty">
      <h1 className="text-2xl font-semibold text-[#10233F]">Coming soon</h1>
      <p className="text-[#566579] mt-2">This page doesn’t have any published content yet.</p>
    </div>
  );
}

